import { onUnmounted, shallowRef, watch } from 'vue'
import throttle from 'lodash/throttle'
import { Statuses } from '@/enum/redesignStatusTypes'
import { allRealtimeExtensions } from '@/composables/useStoreExtensions'
import { useExtensionsStore } from '@voicenter-team/events-sdk-vue-3'
import type { Call, Extension } from '@/types/extension'

const extensionsStore = useExtensionsStore()
const onlineUserIdsRef = shallowRef<number[]>([])

const extensionsAllMapRef = shallowRef<Map<number, Extension[]>>(new Map())
const extensionsOnlineMapRef = shallowRef<Map<number, Extension[]>>(new Map())

let consumerCount = 0
let plainSnapshotCacheByKey = new Map<string, { signature: string, snapshot: Extension }>()

function pushToMapList<K, V> (map: Map<K, V[]>, key: K, value: V): void {
    const existing = map.get(key)
    if (existing) {
        existing.push(value)
    } else {
        map.set(key, [ value ])
    }
}

/**
 * Fingerprint that decides whether a previously cloned plain snapshot can be reused.
 * Includes per-call fields used for table call columns and _UTC_CLIENT epochs; clone only when this changes.
 */
function getExtensionContentSignature (ext: Extension): string {
    const base = [
        ext.userID,
        ext.number,
        ext.userName,
        ext.accountID,
        ext.onlineUserID,
        ext.representative,
        ext.representativeStatus,
        ext.currentRepresentativeLoginStatus,
        ext.summery?.representative ?? '',
        ext.lastCallEventEpoch,
        ext.lastAnsweredCallEventEpoch,
        ext.lastHangupCallEpoch,
        ext.representativeUpdated,
        ext.lastCallEventEpoch_UTC_CLIENT,
        ext.lastAnsweredCallEventEpoch_UTC_CLIENT,
        ext.representativeUpdated_UTC_CLIENT,
        ext.calls.length
    ].join('|')

    if (!ext.calls.length) return base

    const callsSignature = ext.calls.map((call) => [
        call.callStarted,
        call.callStarted_UTC_CLIENT,
        call.callstatus,
        call.answered,
        call.callername,
        call.callerphone,
        call.callType,
        call.direction,
        call.calldurationinterval
    ].join(':')).join(',')

    return `${base}#${callsSignature}`
}

function cloneExtensionPlain (ext: Extension): Extension {
    return {
        ...ext,
        calls: Array.isArray(ext.calls) ? ext.calls.map(cloneCallPlain) : [],
        currentCall: ext.currentCall ? cloneCallPlain(ext.currentCall) : ext.currentCall,
        summery: ext.summery ? { ...ext.summery } : ext.summery
    }
}

function cloneCallPlain (call: Call): Call {
    return {
        ...call,
        recording: call.recording ? { ...call.recording } : call.recording,
        relatedIvrUniqueIDs: Array.isArray(call.relatedIvrUniqueIDs) ? [ ...call.relatedIvrUniqueIDs ] : []
    }
}

function clearExtensionRefs (): void {
    if (onlineUserIdsRef.value.length) onlineUserIdsRef.value = []
    extensionsAllMapRef.value = new Map()
    extensionsOnlineMapRef.value = new Map()
    plainSnapshotCacheByKey = new Map()
}

function rebuild (): void {
    if (consumerCount === 0) return

    const source = allRealtimeExtensions.value
    if (!source?.length) {
        clearExtensionRefs()
        return
    }

    const nextOnlineUserIds: number[] = []
    const nextAllMap = new Map<number, Extension[]>()
    const nextOnlineMap = new Map<number, Extension[]>()
    const nextSnapshotCacheByKey = new Map<string, { signature: string, snapshot: Extension }>()

    for (const ext of source) {
        const shouldUseInOnline = ext.representativeStatus !== Statuses.LOGOUT
        const key = `${ext.userID}:${ext.number}`
        const signature = getExtensionContentSignature(ext)
        const cached = plainSnapshotCacheByKey.get(key)
        const plain = cached?.signature === signature ? cached.snapshot : cloneExtensionPlain(ext)
        nextSnapshotCacheByKey.set(key, { signature, snapshot: plain })

        pushToMapList(nextAllMap, ext.userID, plain)
        if (typeof ext.onlineUserID === 'number' && ext.onlineUserID !== ext.userID) {
            pushToMapList(nextAllMap, ext.onlineUserID, plain)
        }

        if (shouldUseInOnline) {
            nextOnlineUserIds.push(ext.representative)
            pushToMapList(nextOnlineMap, ext.representative, plain)
        }
    }

    plainSnapshotCacheByKey = nextSnapshotCacheByKey
    extensionsAllMapRef.value = nextAllMap
    extensionsOnlineMapRef.value = nextOnlineMap
    onlineUserIdsRef.value = nextOnlineUserIds
}

/** At most one rebuild per second while mutations flood; trailing run applies latest store state. */
const throttledRebuild = throttle(rebuild, 1000, { leading: true, trailing: true })

function scheduleRebuild (): void {
    if (consumerCount === 0) return
    throttledRebuild()
}

watch(extensionsStore.extensions, scheduleRebuild)

export function useRealTableExtension () {
    consumerCount++
    if (consumerCount === 1) scheduleRebuild()

    onUnmounted(() => {
        consumerCount = Math.max(0, consumerCount - 1)
        if (consumerCount === 0) {
            throttledRebuild.cancel()
            clearExtensionRefs()
        }
    })

    return {
        onlineUserIds: onlineUserIdsRef,
        extensionsAllMapRef,
        extensionsOnlineMapRef
    }
}
